/** Admin login: email + password, then 2FA (set up on first login). There is no sign-up page. */
import { APP_NAME } from "@custos/core";
import { Button, CodeInput, CopyButton, Field, Input, Notice, QrCode } from "@custos/ui";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { Navigate, useLocation, useNavigate } from "react-router";
import { AuthLayout } from "../components/AuthLayout";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { errorMessage, isApiError } from "../lib/errors";

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const notice = useAuth((s) => s.notice);
  const setChallenge = useAuth((s) => s.setChallenge);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const login = useMutation({
    mutationFn: () => api.adminLogin(email, password),
    onSuccess: (step) => {
      setChallenge({ token: step.challengeToken, status: step.status });
      navigate("/login/2fa", { state: location.state });
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    login.mutate();
  };
  return (
    <AuthLayout title="Admin login" subtitle="Staff accounts are created by the Owner.">
      <form onSubmit={submit} className="flex flex-col gap-4">
        {notice && <Notice tone="info">{notice}</Notice>}
        <Field label="Email">
          {(p) => (
            <Input
              {...p}
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          )}
        </Field>
        <Field label="Password">
          {(p) => (
            <Input
              {...p}
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          )}
        </Field>
        {login.error && <Notice tone="danger">{errorMessage(login.error)}</Notice>}
        <Button type="submit" block loading={login.isPending}>
          Continue
        </Button>
      </form>
    </AuthLayout>
  );
}

export function TwoFactorPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const challenge = useAuth((s) => s.challenge);
  const setChallenge = useAuth((s) => s.setChallenge);
  const [code, setCode] = useState("");
  const setupNeeded = challenge?.status === "totp_setup_required";
  const setup = useQuery({
    queryKey: ["admin-totp-setup", challenge?.token],
    queryFn: () => api.adminTotpSetup(challenge!.token),
    enabled: setupNeeded,
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
    refetchOnWindowFocus: false,
  });
  const verify = useMutation({
    mutationFn: () =>
      setupNeeded ? api.adminTotpEnable(challenge!.token, code) : api.adminLoginTotp(challenge!.token, code),
    onSuccess: () => {
      setChallenge(null);
      const from = (location.state as { from?: string } | null)?.from;
      navigate(from && from.startsWith("/") ? from : "/", { replace: true });
    },
  });
  if (!challenge) return <Navigate to="/login" replace />;
  const expired =
    isApiError(verify.error, "INVALID_CHALLENGE") || isApiError(setup.error, "INVALID_CHALLENGE");

  return (
    <AuthLayout
      title={setupNeeded ? "Set up admin 2FA" : "Enter your 2FA code"}
      subtitle={
        setupNeeded ? "Required for every admin login and every change." : "From your authenticator app."
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (code.length === 6) verify.mutate();
        }}
        className="flex flex-col gap-5"
      >
        {setupNeeded && (
          <div className="flex flex-col items-center gap-3">
            <p className="text-sm text-muted">
              Scan with your authenticator app (it shows as "{APP_NAME} — Admin …").
            </p>
            {setup.data ? (
              <>
                <QrCode value={setup.data.otpauthUrl} label="Admin 2FA setup QR code" />
                <div className="flex w-full items-center gap-2 rounded-field border border-line bg-soft px-3 py-2">
                  <code
                    className="min-w-0 flex-1 font-mono text-xs break-all text-fg"
                    data-testid="totp-secret"
                  >
                    {setup.data.secret.replace(/(.{4})/g, "$1 ").trim()}
                  </code>
                  <CopyButton value={setup.data.secret} label="Copy key" />
                </div>
              </>
            ) : (
              <div className="size-[208px] animate-pulse rounded-card bg-soft" />
            )}
          </div>
        )}
        <Field label="Authenticator code">
          {(p) => <CodeInput {...p} value={code} onChange={setCode} autoFocus />}
        </Field>
        {expired ? (
          <Notice tone="warning" title="This login step expired">
            Go back and log in again.
          </Notice>
        ) : (
          verify.error && <Notice tone="danger">{errorMessage(verify.error)}</Notice>
        )}
        <Button
          type="submit"
          block
          loading={verify.isPending}
          disabled={code.length !== 6 || (setupNeeded && !setup.data)}
        >
          {setupNeeded ? "Turn on 2FA and continue" : "Verify"}
        </Button>
        <button
          type="button"
          className="text-sm text-muted hover:text-fg"
          onClick={() => {
            setChallenge(null);
            navigate("/login");
          }}
        >
          Back to login
        </button>
      </form>
    </AuthLayout>
  );
}
